import { GetPageQuery } from "apollo"
import { extractProperty } from "./extractProperty"
import { findProperty } from "./findProperty"
import { getNonUmbracoUrl } from "./getNonUmbracoUrl"

type Parent = {
  url: string | null | undefined
  id: number | null | undefined
  level: number | null | undefined
  parent?: Parent
}

export type Breadcrumbs = {
  url?: string
  schemaUrl?: string
  title: string
}[]

function extractTitle(data: any) {
  const pageBlock = extractProperty(data, "pageInfoAndMetaTags")
  const titleSource = pageBlock ? pageBlock?.[0] : data
  const manualTitle = extractProperty(titleSource, "titleBreadcrumbs") as string | undefined

  if (manualTitle) {
    return manualTitle
  }

  const fallbackTitle = extractHeadingTitle(data)
  return fallbackTitle
}

function extractHeadingTitle(data: any) {
  const titleCandidates = [
    "titleH1",
    "mainTitle",
    "articleMainTitle",
    "conetntTitle",
    "blockMainTitle",
    "projectTitle",
  ]

  for (const field of titleCandidates) {
    const candidate = coercePropertyValue(findProperty(data, field))
    if (candidate) {
      return candidate
    }
  }

  const metaTitle = coercePropertyValue(findProperty(data, "metaTitle"))
  if (metaTitle) {
    return metaTitle
  }

  return ""
}

function coercePropertyValue(propertyValue: any) {
  if (!propertyValue) {
    return ""
  }

  if (typeof propertyValue === "string") {
    return propertyValue
  }

  const value = propertyValue?.value || propertyValue?.richText

  return typeof value === "string" ? value : ""
}

export function getBreadCrumbs(data: GetPageQuery, locale: string) {
  const self = extractTitle(data?.page)
  const selfObject: { url: string; title: string } = {
    title: self || "",
    url: getNonUmbracoUrl(data?.page?.url || ""),
  }

  const parent = data?.page?.parent as Parent

  const parents: Breadcrumbs = []

  const hasBlocks = (node: Parent | null | undefined) => {
    const blocks = extractProperty(node, "blocks")
    return Array.isArray(blocks) ? blocks.length > 0 : Boolean(blocks)
  }

  const hasPageTitle = (node: Parent | null | undefined) => {
    const candidates = [findProperty(node, "pageTitle"), findProperty(node, "PageTitle")]
    return candidates.some((candidate) => Boolean(coercePropertyValue(candidate)))
  }

  const hasAnyContent = (node: Parent | null | undefined) => {
    const props = (node as any)?.properties
    if (!Array.isArray(props) || props.length === 0) return false
    return props.some((p: any) => {
      const pv = p.propertyValue
      if (!pv) return false
      return Boolean(
        pv.value ||
          pv.richText ||
          (pv.blocks && pv.blocks.length > 0) ||
          (pv.mediaItems && pv.mediaItems.length > 0) ||
          (pv.elements && pv.elements.length > 0),
      )
    })
  }

  const isClickable = (node: Parent | null | undefined) =>
    hasBlocks(node) || hasPageTitle(node) || hasAnyContent(node)

  function recurse(currentParent: Parent | null) {
    if (
      currentParent &&
      currentParent.level !== null &&
      currentParent.level !== undefined &&
      currentParent.level >= 3
    ) {
      const parentTitle = extractTitle(currentParent)
      if (parentTitle) {
        const resolvedUrl = getNonUmbracoUrl(currentParent.url || "")
        parents.push({
          url: isClickable(currentParent) ? resolvedUrl : undefined,
          schemaUrl: resolvedUrl,
          title: parentTitle,
        })
      }

      if (currentParent.parent) {
        recurse(currentParent.parent)
      }
    }
  }
  recurse(parent ?? null)

  return {
    selfObject,
    parents: [
      {
        title: locale === "he" ? "ראשי" : "Homepage",
        url: locale === "he" ? "/" : "/en",
      },
      ...parents.reverse(),
    ],
  }
}
